import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import './Terms.css';

const Terms = () => {
  const { t } = useTranslation();
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.06 }
    );
    refs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const add = el => { if (el && !refs.current.includes(el)) refs.current.push(el); };

  return (
    <div className="terms-page">
      {/* Hero */}
      <section className="page-hero terms-hero">
        <div className="container page-hero__inner">
          <div className="reveal" ref={add}>
            <div className="section-badge">{t('terms.hero.badge')}</div>
            <h1>{t('terms.hero.title')}</h1>
            <p className="terms-hero__subtitle">{t('terms.hero.subtitle')}</p>
            <p className="terms-hero__updated">{t('terms.hero.lastUpdated')}: March 2026</p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container">
          <div className="terms-content">
            
            {/* 1. Introduction */}
            <div className="terms-section reveal" ref={add}>
              <h2>1. {t('terms.sections.introduction.title')}</h2>
              <p>{t('terms.sections.introduction.content1')}</p>
              <p>{t('terms.sections.introduction.content2')}</p>
            </div>

            {/* 2. Definitions */}
            <div className="terms-section reveal" ref={add}>
              <h2>2. {t('terms.sections.definitions.title')}</h2>
              <ul>
                <li><strong>"{t('terms.sections.definitions.company')}"</strong> - {t('terms.sections.definitions.companyDef')}</li>
                <li><strong>"{t('terms.sections.definitions.customer')}"</strong> - {t('terms.sections.definitions.customerDef')}</li>
                <li><strong>"{t('terms.sections.definitions.services')}"</strong> - {t('terms.sections.definitions.servicesDef')}</li>
                <li><strong>"{t('terms.sections.definitions.products')}"</strong> - {t('terms.sections.definitions.productsDef')}</li>
                <li><strong>"{t('terms.sections.definitions.quotation')}"</strong> - {t('terms.sections.definitions.quotationDef')}</li>
              </ul>
            </div>

            {/* 3. Services Scope */}
            <div className="terms-section reveal" ref={add}>
              <h2>3. {t('terms.sections.services.title')}</h2>
              <p>{t('terms.sections.services.content1')}</p>
              <ul>
                <li>{t('terms.sections.services.item1')}</li>
                <li>{t('terms.sections.services.item2')}</li>
                <li>{t('terms.sections.services.item3')}</li>
                <li>{t('terms.sections.services.item4')}</li>
                <li>{t('terms.sections.services.item5')}</li>
                <li>{t('terms.sections.services.item6')}</li>
              </ul>
              <p>{t('terms.sections.services.content2')}</p>
            </div>

            {/* 4. Quotation & Pricing */}
            <div className="terms-section reveal" ref={add}>
              <h2>4. {t('terms.sections.pricing.title')}</h2>
              <p>{t('terms.sections.pricing.content1')}</p>
              <ul>
                <li>{t('terms.sections.pricing.item1')}</li>
                <li>{t('terms.sections.pricing.item2')}</li>
                <li>{t('terms.sections.pricing.item3')}</li>
                <li>{t('terms.sections.pricing.item4')}</li>
              </ul>
            </div>

            {/* 5. Payment Terms */}
            <div className="terms-section reveal" ref={add}>
              <h2>5. {t('terms.sections.payment.title')}</h2>
              <p>{t('terms.sections.payment.content1')}</p>
              <ul>
                <li>{t('terms.sections.payment.item1')}</li>
                <li>{t('terms.sections.payment.item2')}</li>
                <li>{t('terms.sections.payment.item3')}</li>
              </ul>
              <p>{t('terms.sections.payment.content2')}</p>
            </div>

            {/* 6. Installation Terms */}
            <div className="terms-section reveal" ref={add}>
              <h2>6. {t('terms.sections.installation.title')}</h2>
              <p>{t('terms.sections.installation.content1')}</p>
              <ul>
                <li>{t('terms.sections.installation.item1')}</li>
                <li>{t('terms.sections.installation.item2')}</li>
                <li>{t('terms.sections.installation.item3')}</li>
                <li>{t('terms.sections.installation.item4')}</li>
                <li>{t('terms.sections.installation.item5')}</li>
              </ul>
            </div>

            {/* 7. Warranty Policy */}
            <div className="terms-section reveal" ref={add}>
              <h2>7. {t('terms.sections.warranty.title')}</h2>
              <p>{t('terms.sections.warranty.content1')}</p>
              <h3>{t('terms.sections.warranty.covered')}</h3>
              <ul>
                <li>{t('terms.sections.warranty.covered1')}</li>
                <li>{t('terms.sections.warranty.covered2')}</li>
                <li>{t('terms.sections.warranty.covered3')}</li>
              </ul>
              <h3>{t('terms.sections.warranty.notCovered')}</h3>
              <ul>
                <li>{t('terms.sections.warranty.notCovered1')}</li>
                <li>{t('terms.sections.warranty.notCovered2')}</li>
                <li>{t('terms.sections.warranty.notCovered3')}</li>
                <li>{t('terms.sections.warranty.notCovered4')}</li>
                <li>{t('terms.sections.warranty.notCovered5')}</li>
              </ul>
            </div>

            {/* 8. AMC Terms */}
            <div className="terms-section reveal" ref={add}>
              <h2>8. {t('terms.sections.amc.title')}</h2>
              <p>{t('terms.sections.amc.content1')}</p>
              <ul>
                <li>{t('terms.sections.amc.item1')}</li>
                <li>{t('terms.sections.amc.item2')}</li>
                <li>{t('terms.sections.amc.item3')}</li>
                <li>{t('terms.sections.amc.item4')}</li>
                <li>{t('terms.sections.amc.item5')}</li>
              </ul>
            </div>

            {/* 9. Cancellation & Refund */}
            <div className="terms-section reveal" ref={add}>
              <h2>9. {t('terms.sections.cancellation.title')}</h2>
              <p>{t('terms.sections.cancellation.content1')}</p>
              <ul>
                <li>{t('terms.sections.cancellation.item1')}</li>
                <li>{t('terms.sections.cancellation.item2')}</li>
                <li>{t('terms.sections.cancellation.item3')}</li>
                <li>{t('terms.sections.cancellation.item4')}</li>
              </ul>
            </div>

            {/* 10. Customer Responsibilities */}
            <div className="terms-section reveal" ref={add}>
              <h2>10. {t('terms.sections.responsibilities.title')}</h2>
              <p>{t('terms.sections.responsibilities.content1')}</p>
              <ul>
                <li>{t('terms.sections.responsibilities.item1')}</li>
                <li>{t('terms.sections.responsibilities.item2')}</li>
                <li>{t('terms.sections.responsibilities.item3')}</li>
                <li>{t('terms.sections.responsibilities.item4')}</li>
                <li>{t('terms.sections.responsibilities.item5')}</li>
              </ul>
            </div>

            {/* 11. Privacy & Data Protection */}
            <div className="terms-section reveal" ref={add}>
              <h2>11. {t('terms.sections.privacy.title')}</h2>
              <p>{t('terms.sections.privacy.content1')}</p>
              <ul>
                <li>{t('terms.sections.privacy.item1')}</li>
                <li>{t('terms.sections.privacy.item2')}</li>
                <li>{t('terms.sections.privacy.item3')}</li>
                <li>{t('terms.sections.privacy.item4')}</li>
              </ul>
              <p>{t('terms.sections.privacy.content2')}</p>
            </div>

            {/* 12. Limitation of Liability */}
            <div className="terms-section reveal" ref={add}>
              <h2>12. {t('terms.sections.liability.title')}</h2>
              <p>{t('terms.sections.liability.content1')}</p>
              <ul>
                <li>{t('terms.sections.liability.item1')}</li>
                <li>{t('terms.sections.liability.item2')}</li>
                <li>{t('terms.sections.liability.item3')}</li>
                <li>{t('terms.sections.liability.item4')}</li>
              </ul>
            </div>

            {/* 13. Dispute Resolution */}
            <div className="terms-section reveal" ref={add}>
              <h2>13. {t('terms.sections.dispute.title')}</h2>
              <p>{t('terms.sections.dispute.content1')}</p>
              <ul>
                <li>{t('terms.sections.dispute.item1')}</li>
                <li>{t('terms.sections.dispute.item2')}</li>
                <li>{t('terms.sections.dispute.item3')}</li>
              </ul>
            </div>

            {/* 14. Governing Law */}
            <div className="terms-section reveal" ref={add}>
              <h2>14. {t('terms.sections.governing.title')}</h2>
              <p>{t('terms.sections.governing.content1')}</p>
              <p>{t('terms.sections.governing.content2')}</p>
            </div>

            {/* 15. Contact Information */}
            <div className="terms-section reveal" ref={add}>
              <h2>15. {t('terms.sections.contact.title')}</h2>
              <p>{t('terms.sections.contact.content1')}</p>
              <div className="terms-contact-info">
                <p><strong>ShivShri Enterprises</strong></p>
                <p>Gargoti, Tal. Bhudargad, Dist. Kolhapur</p>
                <p>Maharashtra - 416502, India</p>
                <p>Phone: +91 94217 22575</p>
                <p>Email: shivshrienterprises2628@gmail.com</p>
              </div>
            </div>

            {/* Acceptance */}
            <div className="terms-acceptance reveal" ref={add}>
              <p>{t('terms.acceptance')}</p>
            </div>

          </div>

          {/* Back to Home */}
          <div className="terms-actions reveal" ref={add}>
            <Link to="/" className="btn btn-primary">{t('terms.backToHome')}</Link>
            <Link to="/contact" className="btn btn-green">{t('terms.contactUs')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
